import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import {
  LeftCircleOutlined,
  PrinterOutlined,
  SignatureOutlined,
} from '@ant-design/icons';
import { AButton, ATitle, Loading, ToSignature } from '../../../components';
import { ContainerMenuPrint } from './styles';
import {
  IDentalTreatment,
  IDentalTreatmentItem,
} from '../../../shared/interfaces';
import { useConfiguration } from '../../../hooks/configuration';
import { useToast } from '../../../hooks/toast';
import { getDentalTreatment } from '../../../http';
import { DentalTreatmentReport } from '../../../shared/reports/DentalTreatment';
import api from '../../../services/api';
import { hasAllowedModule } from '../../../shared/utils/modules';

const DentalTreatmentPrint: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { configuration } = useConfiguration();
  const [treatment, setTreatment] = useState<IDentalTreatment | null>(null);
  const [items, setItems] = useState<IDentalTreatmentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [modalSign, setModalSign] = useState<boolean>(false);
  const [validationCode, setValidationCode] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [signerName, setSignerName] = useState<string | null>(null);
  const [signatureMode, setSignatureMode] = useState<string | null>(null);

  const loadTreatment = useCallback(async () => {
    if (!id) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'ID do tratamento não encontrado.',
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const treatmentData = await getDentalTreatment(id);
      const { treatment, items } = treatmentData;

      const isDigitallySigned = Boolean(
        treatment?.signature_validation_code || treatment?.signed_at,
      );

      let signedProfessionalName: string | null = null;
      let signedProfessionalSignature: string | null = null;

      if (treatment?.professional_id) {
        const professionalResponse = await api.get(
          `/professionals/${treatment.professional_id}`,
        );
        signedProfessionalName = professionalResponse.data?.name || null;

        if (isDigitallySigned) {
          signedProfessionalSignature =
            professionalResponse.data?.signature || null;
        }
      }

      setTreatment(treatment);
      setItems(items);
      setValidationCode(treatment?.signature_validation_code || null);
      setSignerName(treatment?.professional_name || signedProfessionalName);
      setSignature(signedProfessionalSignature);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar dados',
        description:
          error?.response?.data?.error ??
          'Não foi possível carregar o tratamento.',
      });
    } finally {
      setLoading(false);
    }
  }, [id, addToast]);

  useEffect(() => {
    loadTreatment();
  }, [loadTreatment]);

  if (loading) return <Loading load />;

  const isDigitallySigned = Boolean(validationCode || treatment?.signed_at);
  const canUseDigitalSignature = hasAllowedModule(
    configuration?.allowed_modules,
    'assinatura_digital',
  );

  return (
    <>
      <ATitle icon={<PrinterOutlined />}>
        Imprimir Tratamento Odontológico
      </ATitle>
      <ContainerMenuPrint>
        <AButton
          type="default"
          onClick={() => navigate(-1)}
          icon={<LeftCircleOutlined />}
        >
          Voltar
        </AButton>
        {!isDigitallySigned && canUseDigitalSignature && (
          <AButton
            type="dashed"
            onClick={() => setModalSign(true)}
            icon={<SignatureOutlined />}
            style={{ backgroundColor: '#33cf5c', color: '#fff' }}
          >
            Assinar tratamento
          </AButton>
        )}
      </ContainerMenuPrint>
      {validationCode && (
        <p style={{ marginTop: 8, fontWeight: 600 }}>
          Código de validação: {validationCode}
        </p>
      )}
      {signatureMode && (
        <p style={{ marginTop: 4, fontWeight: 600 }}>
          Modo da assinatura:{' '}
          {signatureMode === 'A1' ? 'Assinado com A1' : 'Assinatura interna'}
        </p>
      )}
      <DentalTreatmentReport
        treatment={treatment}
        items={items}
        signerName={signerName}
        signature={signature}
        validationCode={validationCode || treatment?.signature_validation_code}
      />
      <ToSignature
        open={modalSign && canUseDigitalSignature}
        close={() => setModalSign(false)}
        professional_id={treatment?.professional_id}
        onAuthenticated={async (authData, password) => {
          const signResponse = await api.post('/signature-validation/sign', {
            document_type: 'DENTAL_TREATMENT',
            record_id: treatment?.id || id,
            professional_id: treatment?.professional_id,
            password,
          });

          setSignature(
            authData.signature ||
              signResponse.data.professional_signature ||
              null,
          );
          setValidationCode(signResponse.data.validation_code || null);
          setSignatureMode(signResponse.data.signature_mode || null);
          setSignerName(
            signResponse.data?.professional?.name ||
              treatment?.professional_name ||
              signerName,
          );
          addToast({
            type: 'success',
            title: 'Tratamento assinado',
            description:
              'Tratamento odontológico assinado digitalmente com sucesso.',
          });
        }}
      />
    </>
  );
};

export default DentalTreatmentPrint;
